/**
 * useTokenRetry — 401 → 重新登入(login / ticketLogin)重試流程。
 *
 * 抽自 App.vue 的「網路重試 Toast + refreshToken」邏輯。
 *
 * 📝 toast 文案「网络繁忙，正在重试...」語意上其實是 token 續期（重新登入），
 *    不是網路問題 —— 2026-07-30 確認**刻意維持**這個說法（玩家聽得懂、不會慌），
 *    不要「順手修正」它。key 在自動產生的 locales/zh.ts（retry.toast），也不可手改。最多重試 3 次,
 * 每次失敗等 800ms 再來,過程中顯示「網路繁忙,正在重試...(第 N 次)」toast。
 * 全部失敗就 escalate 到 onTerminalFail(切全屏錯誤頁、關掉所有 modal)。
 *
 * # 觸發來源(兩個,共用同一輪)
 *
 *   1. Cocos 送 `AUTH_INVALID`(postMessage)
 *   2. host 自己打的 API 收到 401 —— `http.ts` 攔截器透過 `setAuthRecoveryHandler`
 *      註冊進來的就是本檔的 `refreshToken`
 *
 * ⚠️ **single-flight 回傳同一個 in-flight promise**(不是直接 return)。
 *    因為 `authStore.bootstrap()` 在 balance 收 401 後會 `await runAuthRecovery()`,
 *    而那一輪通常已被 http 攔截器搶先觸發 —— 直接 return 會讓 bootstrap 拿到
 *    false 而誤判成「救不回來」。
 *
 * # 為什麼抽出來
 *
 * 整個 retry 邏輯 self-contained:state(retryToastVisible / retryCount /
 * isRefreshing / resetRetryTimer)只服務 toast 顯示,refreshToken 是純 IO 動作。
 * 從 App.vue 抽出後省 ~70 行,而且未來想加 retry telemetry / 指數 backoff 都在
 * 這一支動,不會碰到 modal / postMessage 等其他關注點。
 *
 * # 用法(App.vue)
 *
 *   const tokenRetry = useTokenRetry({
 *     doRefreshToken,
 *     authError,
 *     onSuccess: () => {
 *       postMessage.sendTokenToGame()
 *     },
 *     onTerminalFail: () => handleAuthInvalid(() => modal.closeAllModals()),
 *     t,
 *   })
 *
 *   // 在 postMessage onAuthInvalid 內 call:
 *   onAuthInvalid: () => tokenRetry.refreshToken(),
 *
 *   // template:
 *   <Transition name="retry-toast-fade">
 *     <div v-if="tokenRetry.retryToastVisible.value" class="retry-toast">
 *       {{ tokenRetry.retryToastText.value }}
 *     </div>
 *   </Transition>
 */

import { computed, ref, type ComputedRef, type Ref } from 'vue'

const MAX_REFRESH_ATTEMPTS = 3
const RETRY_DELAY_MS = 800
/** Toast 淡出動畫完成後再把 count 歸零的 buffer */
const RESET_COUNT_DELAY_MS = 600

/**
 * 英文序數(1st / 2nd / 3rd / 4th ...)。i18n 模板 `{ordinal}` 用。
 */
function ordinalEn(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || 'th')
}

type TFunction = (key: string, params?: Record<string, unknown>) => string

export interface UseTokenRetryDeps {
  /** 真正重新登入拿 token 的 IO 動作(useAuth 提供) */
  doRefreshToken: () => Promise<boolean>
  /** 進入終態的旗標 — true 時不再啟動重試(已被 onTerminalFail 標掉) */
  authError: Readonly<Ref<unknown>>
  /**
   * 重試成功 callback。caller 在此 dispatch 新 token:
   *   - postMessage.sendTokenToGame()
   */
  onSuccess: () => void
  /**
   * 重試 N 次全部失敗的終態 callback。caller 在此通常 call:
   *   handleAuthInvalid(() => modal.closeAllModals())
   * 把畫面切到全屏錯誤頁 + 關掉所有 modal。
   */
  onTerminalFail: () => void
  /** vue-i18n 的 t 函式 — 用 `retry.toast` key + `{count, ordinal}` placeholder */
  t: TFunction
}

export interface UseTokenRetryReturn {
  /** Toast 是否顯示(進 refreshToken 翻 true,離開 finally 翻 false) */
  retryToastVisible: Readonly<Ref<boolean>>
  /** Toast 顯示文字,e.g. 「网络繁忙,正在重试(第 2 次 / 2nd)...」 */
  retryToastText: ComputedRef<string>
  /**
   * 觸發重新登入 — 由 postMessage onAuthInvalid 與 http.ts 401 攔截器共用。
   * @returns 是否換到新 token(single-flight:併發呼叫共用同一輪結果)
   */
  refreshToken: () => Promise<boolean>
}

export function useTokenRetry(deps: UseTokenRetryDeps): UseTokenRetryReturn {
  const { doRefreshToken, authError, onSuccess, onTerminalFail, t } = deps

  const retryToastVisible = ref(false)
  const retryCount = ref(0)
  let inflight: Promise<boolean> | null = null
  let resetRetryTimer: ReturnType<typeof setTimeout> | null = null

  /** 真正跑一輪重試(不含 single-flight 判斷) */
  async function runRefreshRound(): Promise<boolean> {
    // 有待 reset 的計時器先清掉,避免新一輪 retry 被舊計時器歸零
    if (resetRetryTimer) {
      clearTimeout(resetRetryTimer)
      resetRetryTimer = null
    }
    retryCount.value = 0
    retryToastVisible.value = true

    let success = false
    try {
      for (let i = 1; i <= MAX_REFRESH_ATTEMPTS; i++) {
        retryCount.value = i
        success = await doRefreshToken()
        if (success) break
        // 非最後一次失敗後稍作等待再重試
        if (i < MAX_REFRESH_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS))
        }
      }

      if (success) {
        onSuccess()
      } else {
        // N 次全部失敗 → 終態,caller 切錯誤頁 + 關所有 modal
        onTerminalFail()
      }
      return success
    } finally {
      retryToastVisible.value = false
      // Toast 淡出動畫(retry-toast-fade)完成後再把 count 歸零,
      // 避免 toast 還在淡出時 retryCount 已經跳成 0 文字閃一下
      resetRetryTimer = setTimeout(() => {
        retryCount.value = 0
        resetRetryTimer = null
      }, RESET_COUNT_DELAY_MS)
    }
  }

  /**
   * 重新登入(login / ticketLogin)拿新 token,成功就 dispatch 給遊戲,
   * 失敗 N 次就 escalate 到終態。
   *
   * 防 concurrency:
   *   - 已經在跑 → **回傳同一個 in-flight promise**(不是直接 return)。
   *     併發 401、或「http 攔截器已觸發 + bootstrap 想 await 結果」都共用這一輪,
   *     不會重複登入,也不會有人拿到假的 false。
   *   - 已經終態失效(authError)→ 直接 false(避免在錯誤頁背景繼續跑 retry)
   */
  function refreshToken(): Promise<boolean> {
    if (authError.value) return Promise.resolve(false)
    if (inflight) return inflight

    inflight = runRefreshRound().finally(() => {
      inflight = null
    })
    return inflight
  }

  const retryToastText = computed(() =>
    t('retry.toast', { count: retryCount.value || 1, ordinal: ordinalEn(retryCount.value || 1) }),
  )

  return {
    retryToastVisible,
    retryToastText,
    refreshToken,
  }
}
